import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { getBrandTheme } from "../../styles/brandTheme";

const GOAL_LABELS = {
  lose:     "Lose weight",
  maintain: "Maintain weight",
  gain:     "Gain weight",
};

export default function PendingUpdatePrompt({ pendingQuizData, session, onComplete }) {
  const appTheme = getBrandTheme();
  const [loading, setLoading] = useState(false);

  const { data, targets } = pendingQuizData;
  const userId = session?.user?.id;

  if (!data || !targets) return null;

  async function handleUpdate() {
    setLoading(true);
    await supabase.from("user_profiles").upsert({
      id:                 userId,
      name:               data.name      || undefined,
      goal:               data.goal      || null,
      gender:             data.gender    || null,
      dob:                data.dob       || null,
      height:             data.height    || null,
      current_weight:     data.currentWeight || null,
      activity_level:     data.activityLevel || null,
      daily_calorie_goal: targets?.calories ?? null,
      daily_protein_goal: targets?.protein  ?? null,
      daily_carbs_goal:   targets?.carbs    ?? null,
      daily_fats_goal:    targets?.fats     ?? null,
    }, { onConflict: "id" })
      .then(({ error }) => {
        if (error) console.error("[PendingUpdatePrompt] upsert error:", error.message);
      });
    setLoading(false);
    onComplete(true);
  }

  function handleKeep() {
    onComplete(false);
  }

  // Build a one-line summary of the key changes
  const summaryParts = [];
  if (data.goal)      summaryParts.push(GOAL_LABELS[data.goal] ?? data.goal);
  if (targets?.calories) summaryParts.push(`${targets.calories.toLocaleString()} kcal`);
  if (targets?.protein)  summaryParts.push(`Protein ${targets.protein}g`);

  return (
    <div style={{
      minHeight: "100vh",
      background: appTheme.pageBackground,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px 24px 52px",
    }}>
      <div style={{ width: "100%", maxWidth: "400px", display: "flex", flexDirection: "column", gap: "0" }}>

        {/* Heading */}
        <h1 style={{
          fontFamily: appTheme.fontHeading,
          fontSize: "30px",
          fontWeight: "600",
          color: appTheme.primaryText,
          margin: "0 0 12px",
          lineHeight: 1.2,
        }}>
          Update your targets?
        </h1>

        {/* Body */}
        <p style={{
          margin: "0 0 20px",
          fontFamily: appTheme.fontBody,
          fontSize: "15px",
          color: appTheme.secondaryText,
          lineHeight: 1.6,
        }}>
          You completed the setup quiz. Would you like to update your profile with your latest information?
        </p>

        {/* Summary card */}
        {summaryParts.length > 0 && (
          <div style={{
            padding: "14px 16px",
            borderRadius: "14px",
            background: appTheme.cardBackground,
            border: appTheme.cardBorder,
            marginBottom: "28px",
          }}>
            <div style={{
              fontFamily: appTheme.fontBody,
              fontSize: "13px",
              fontWeight: "600",
              color: appTheme.mutedText,
              marginBottom: "6px",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}>
              Your quiz results
            </div>
            <div style={{
              fontFamily: appTheme.fontBody,
              fontSize: "15px",
              color: appTheme.primaryText,
              fontWeight: "500",
              lineHeight: 1.5,
            }}>
              {summaryParts.join(" · ")}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <button
            onClick={handleUpdate}
            disabled={loading}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: "14px",
              border: "none",
              background: appTheme.accentColor,
              color: "#fff",
              fontFamily: appTheme.fontBody,
              fontWeight: "700",
              fontSize: "16px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              touchAction: "manipulation",
            }}
          >
            {loading ? "Updating…" : "Yes, update my targets"}
          </button>
          <button
            onClick={handleKeep}
            disabled={loading}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: "14px",
              border: `1px solid ${appTheme.tealMid}`,
              background: "transparent",
              color: appTheme.secondaryText,
              fontFamily: appTheme.fontBody,
              fontWeight: "600",
              fontSize: "16px",
              cursor: loading ? "not-allowed" : "pointer",
              touchAction: "manipulation",
            }}
          >
            Keep my existing targets
          </button>
        </div>
      </div>
    </div>
  );
}
